import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNetworkStatus } from "./useNetworkStatus";

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  retryCount: number;
}
/**
 * Reads the session Supabase persisted in localStorage. Used as a fallback when
 * `getSession()` stalls (observed in iOS WebViews), so the app never hangs on a
 * permanent loading screen for an already signed-in user.
 */
function readPersistedSession(): Session | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !/^sb-.*-auth-token$/.test(key)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const session = (parsed?.currentSession ?? parsed) as Session | null;
      if (session?.access_token) return session;
    }
  } catch {
    // Ignore parse/storage errors — treat as signed out.
  }
  return null;
}


export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    session: null,
    loading: true,
    error: null,
    retryCount: 0
  });
  const { hasConnection, retry: retryNetwork } = useNetworkStatus();

  const retry = async () => {
    await retryNetwork();
    // After network retry, the useEffect will re-run due to hasConnection change
  };

  useEffect(() => {
    let active = true;
    let subscription: any;

    const setupAuth = async () => {
      if (!active) return;

      if (!hasConnection) {
        setAuthState(prev => ({
          ...prev,
          error: "No internet connection. Please check your network and try again.",
          loading: false,
        }));
        return;
      }

      try {
        // Set up auth state listener FIRST
        const { data } = supabase.auth.onAuthStateChange((event, session) => {
          if (active) {
            setAuthState(prev => ({
              ...prev,
              session: session ?? null,
              loading: false,
              error: null,
              retryCount: 0,
            }));
          }
        });
        subscription = data.subscription;

        // THEN get current session (needed for initial load before any auth event fires).
        // On iOS WebViews getSession() can hang indefinitely (storage lock), which would
        // leave protected routes stuck on "Loading…" — so race it with a timeout and
        // fall back to the persisted session in localStorage.
        const currentSession = await Promise.race([
          supabase.auth.getSession().then(({ data }) => data.session ?? null),
          new Promise<Session | null>((resolve) =>
            setTimeout(() => resolve(readPersistedSession()), 5000),
          ),
        ]);

        if (active) {
          setAuthState(prev => ({
            ...prev,
            session: currentSession ?? null,
            loading: false,
            error: null,
            retryCount: 0,
          }));
        }

      } catch (error) {
        if (active) {
          setAuthState(prev => ({
            ...prev,
            loading: false,
            error: "Failed to initialize authentication",
          }));
        }
      }
    };

    setupAuth();

    return () => {
      active = false;
      subscription?.unsubscribe();
    };
  }, [hasConnection]);

  return {
    session: authState.session,
    loading: authState.loading,
    error: authState.error,
    retryCount: authState.retryCount,
    retry,
    ready: Boolean(supabase) && hasConnection
  } as const;
}
